import React, { useEffect, useState, useRef } from "react";
import Styles from "./Element.module.css";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import { useSelector } from "react-redux";
import DateInput from "../../../../commons/Inputs/DateInput/DateInput";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { verifyCompanyDisplayName } from "../../../../Services/ApiCalling/Auth";
import { toast } from "react-toastify";
import { toastProperties } from "../../../../commons/Toast/Toaster";
import {
  TAX_CONFIG,
  TAX_FIELDS,
  checkTaxInput,
} from "../../../../helpers/validators/taxValidators";
import { getIfscDetails } from "../../../../Services/ApiCalling/PublicApi";
import {
  sanitizeContactName,
  CONTACT_NAME_REGEX,
  ACCOUNT_NUMBER_REGEX,
  GENERAL_ERRORS,
  sanitizeAccountNumber,
  sanitizeGeneralText,
  limitWords,
} from "../../../../helpers/validators/generalValidators";


const CompanyDetails = ({ userData, handleData, handleProceed }) => {
  const publicData = useSelector((state) => state?.publicSlice);

  const initialState = {
    holdingCompany:
      userData?.holdingCompany?.id || userData?.holdingCompany || "",
    traderType: userData?.traderType || {},
    companyName: userData?.companyName || "",
    displayName: userData?.displayName || "",
    companyDescription: userData?.companyDescription || "",
    category: userData?.category || {},
    registration: userData?.registration || "",
    pan: userData?.pan || "",
    tan: userData?.tan || "",
    gst: userData?.gst || "",
    license: userData?.license || "",
    bankName: userData?.bankName || "",
    bankAddress: userData?.bankAddress || "",
    payee: userData?.payee || "",
    accountNo: userData?.accountNo || "",
    ifsc: userData?.ifsc || "",
    rating: userData?.rating || {},
    ratingYear: userData?.ratingYear || "",
    agency: userData?.agency || "",
    byRules: false,
    byLaw: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const [ifscLoading, setIfscLoading] = useState(false);
  const [ifscError, setIfscError] = useState("");
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const latestSearch = useRef("");


  const handleChange = (field, value) => {
    const newErrors = { ...errors };

    let formattedValue = TAX_FIELDS.includes(field)
      ? checkTaxInput(field, value)
      : value;
    if (field === "payee") {
      formattedValue = sanitizeContactName(formattedValue).slice(0, 50);
    }

    if (field === "companyName") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 100);
    }
    if (field === "displayName") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 50);
    }
    if (field === "companyDescription") {
      const sanitized = sanitizeGeneralText(formattedValue);
      const limited = limitWords(sanitized, 250);
      if (sanitized !== limited) {
        toast.info("Company Description is limited to 250 words", {
          ...toastProperties,
          toastId: "company-desc-limit",
        });
      }
      formattedValue = limited;
    }
    if (field === "bankName") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 100);
    }
    if (field === "bankAddress") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 250);
    }
    if (["ratingYear", "agency"].includes(field)) {
      formattedValue = sanitizeGeneralText(formattedValue);
    }
  
    if (field === "accountNo") {
      formattedValue = sanitizeAccountNumber(formattedValue);
    }

    if (formattedValue) delete newErrors[field];

    const fieldDependencies = {
      displayName: "companyName",
      companyDescription: "displayName",
      holdingCompany: "displayName",
      category: "displayName",
      registration: "category",
      tan: "registration",
      gst: "tan",
      pan: "gst",
      license: "pan",
      bankName: "license",
      bankAddress: "bankName",
      accountNo: "bankAddress",
      payee: "accountNo",
      ifsc: "payee",
      rating: "ifsc",
      ratingYear: "rating",
      agency: "ratingYear",
    };

    if (value) {
      delete newErrors[field];
    }

    const dependentField = fieldDependencies[field];
    if (dependentField && !formData[dependentField]) {
      newErrors[dependentField] = `${dependentField
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())} is required`;
    }

    setFormData((prev) => {
      const newData = { ...prev, [field]: formattedValue };

      return newData;
    });
    setErrors(newErrors);
  };

  const handleIfscChange = async (value) => {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
      handleChange("ifsc", value);
      setIfscError("");
  
      if (!IFSC_REGEX.test(value)) return;
  
      latestSearch.current = value;
      const currentSearch = value;
  
      setIfscLoading(true);
      const res = await getIfscDetails(value);
  
      if (latestSearch.current !== currentSearch) {
      setIfscLoading(false);
      return;
      }

  
      if (res && res.data && res.data.code === 200) {
        const data = res.data.data;
        if (!data || !data.bank || !data.branch) {
      
          setIfscLoading(false);
          return;
        }
  
      //   const { bank, branch } = data;
  
      //   setFormData((prev) => ({
      //     ...prev,
      //     bankName: (!prev.bankName || autoFilledFields.has("bankName")) ? bank : prev.bankName,
      //     bankAddress: (!prev.bankAddress || autoFilledFields.has("bankAddress")) ? branch : prev.bankAddress,
      //   }));
  
      //   setErrors((prevErrors) => {
      //     const nextErrors = { ...prevErrors };
      //     delete nextErrors.bankName;
      //     delete nextErrors.bankAddress;
      //     return nextErrors;
      //   });
  
        setAutoFilledFields(new Set(["bankName", "bankAddress"]));
      } 
      
      else {
        const errorMsg = res?.data?.message || res?.message || "Could not fetch bank details.";
        setIfscError(errorMsg);
      }
      
      setIfscLoading(false);
    };

  const unlockField = (fieldName) => {
    setAutoFilledFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };


  const handleBlur = (field)=>{
      const config = TAX_CONFIG[field];
      if (!config) return;
      const value = formData[field];
      if (!value) return; 
  setErrors((prev) => {
    const next = { ...prev };
    if (!config.validate(value)) {
      next[field] = config.error; 
    } else {
      delete next[field];         
    }
    return next;
  });
  }

  const validateFields = async () => {
    const requiredFields = [
      "companyName",
      "displayName",
      "registration",
      "pan",
      "tan",
      "gst",
      "bankName",
      "bankAddress",
      "payee",
      "accountNo",
      "ifsc",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())} is required`;
      }

      if (formData.accountNo && !ACCOUNT_NUMBER_REGEX.test(formData.accountNo)) {
        newErrors.accountNo = GENERAL_ERRORS.accountNumber;
      }
    });

    if (formData.ifsc && !IFSC_REGEX.test(formData.ifsc)) {
      newErrors.ifsc = GENERAL_ERRORS.ifsc;
    }


    if (formData.payee && !CONTACT_NAME_REGEX.test(formData.payee)) {
      newErrors.payee = GENERAL_ERRORS.payee;
    } else if (formData.payee && formData.payee.length > 50) {
      newErrors.payee = "Payee name cannot exceed 50 characters";
    }

    if (formData.companyName && formData.companyName.length > 100) {
      newErrors.companyName = "Company name cannot exceed 100 characters";
    }

    if (formData.displayName && formData.displayName.length > 50) {
      newErrors.displayName = "Display name cannot exceed 50 characters";
    }

    if (formData.bankName && formData.bankName.length > 100) {
      newErrors.bankName = "Bank name cannot exceed 100 characters";
    }

    if (formData.bankAddress && formData.bankAddress.length > 250) {
      newErrors.bankAddress = "Bank address cannot exceed 250 characters";
    }

    if (formData.companyDescription) {
      const words = formData.companyDescription.trim().split(/\s+/).filter(Boolean);
      if (words.length > 250) {
        newErrors.companyDescription = "Company Description cannot exceed 250 words";
      }
    }

    
    TAX_FIELDS.forEach((field) => {
      const value = formData[field];
      const config = TAX_CONFIG[field];
      if (value && config && !config.validate(value)) {
        newErrors[field] = config.error;
      }
    });

    if (formData?.displayName) {
      try {
        const res = await verifyCompanyDisplayName(formData?.displayName);
        const responseData = res?.data;

        if (responseData?.code !== 200) {
          newErrors.displayName =
            responseData?.message || "Display name not available";
        }
      } catch (err) {
        newErrors.displayName = "Could not verify display name";
      }
    }

    // if (
    //   !formData.holdingCompany ||
    //   (!formData.holdingCompany?.id &&
    //     typeof formData.holdingCompany !== "string")
    // ) {
    //   newErrors.holdingCompany = "Holding Company is required";
    // }

    if (!formData.category?.id) {
      newErrors.category = "Category is required";
    }
    const hasAnyRatingData = formData.rating?.id || formData.ratingYear || formData.agency;
    
    if(hasAnyRatingData){
    if (!formData.rating?.id) {
      newErrors.rating = "Rating is required";
    }

    if (!formData.ratingYear) {
      newErrors.ratingYear = "Credit Rating Year is required";
    }

    if (!formData.agency) {
      newErrors.agency = "Credit Rating Agency is required";
    }
  }
    if (!formData.byRules) {
      newErrors.byRules = "You must accept the Business Rules";
    }
    if (!formData.byLaw) {
      newErrors.byLaw = "You must accept the Bye Laws";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Password does not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async (data) => {
    if (ifscLoading) return;
    const isValid = await validateFields();
    if (!isValid) return;
    const userCompany = {
      ...formData,
      roles: userData?.profile?.value,
      holdingCompanyId: formData.holdingCompany?.id || formData.holdingCompany,
      categoryId: formData.category?.id || formData.category,
      bankAccounts: {
        nameAddress: `${formData.bankName} ${formData.bankAddress}`,
        payee: formData.payee,
        accNo: formData.accountNo,
        ifsc: formData.ifsc,
        operationalAt: "",
      },
      creditRatingId: formData.rating?.id || formData.rating,
      creditRatingYear: formData.ratingYear,
      creditRatingAgency: formData.agency,
      isAcceptedByLaws: formData.byLaw,
      isAcceptedBusinessRule: formData.byRules,
    };

    handleData("userCompany", userCompany);
    handleProceed(data);
  };

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
  ) => {
    const taxConfig = TAX_CONFIG[field];
    let maxLength = undefined;
    if (field === "companyName") maxLength = 100;
    if (field === "displayName") maxLength = 50;
    if (field === "payee") maxLength = 50;
    if (field === "bankName") maxLength = 100;
    if (field === "bankAddress") maxLength = 250;
    if (field === "accountNo") maxLength = 18;
    if (taxConfig?.maxLength) maxLength = taxConfig.maxLength;

    return(
    <TextInput
      heading={`${heading} `}
      value={formData[field]}
      onChange={(value) => handleChange(field, value)}
      onBlur={()=>handleBlur(field)}
      maxLength={maxLength}
      example={taxConfig?.example??example}
      inputStyle={taxConfig ? { textTransform: "uppercase" } : {}}
      type={type}
      height="45"
      error={errors[field]}
      readOnly={readOnly}
    />
    )
  };

  const renderAutofillInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false
  ) => {
    const isAutoFilled = autoFilledFields.has(field);
    let maxLength = undefined;
    if (field === "bankName") maxLength = 100;
    if (field === "bankAddress") maxLength = 250;
    if (field === "payee") maxLength = 50;

    return (
      <div style={{ position: "relative" }}> 
        <TextInput
          heading={`${heading} `}
          value={formData[field]}
          onChange={(value) => handleChange(field, value)}
          maxLength={maxLength}
          example={example}
          type={type}
          height="45"
          error={errors[field]}
          readOnly={readOnly}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px"
            }}
          >
            🖊 Edit
          </span>
        )}
      </div>
    );
  };

  const renderIfscInput = () => (
    <div>
      <TextInput
        heading="IFSC Code *"
        value={formData.ifsc}
        onChange={(value) => handleIfscChange(value)}
        maxLength={11}
        example={ifscLoading ? "Fetching bank details..." : "Enter IFSC Code"}
        height="45"
        error={errors.ifsc || ifscError}
      />
      {ifscLoading && (
        <span style={{ fontSize: "12px", color: "var(--primary-color-text)", marginTop: "4px", display: "block" }}>
          🔍 Looking up bank details...
        </span>
      )}
    </div>
  );

  const renderSelectInput = (field, heading, list, example, disabled = false) => (
    <SelectInput
      onChange={(value) => handleChange(field, value)}
      list={list}
      value={formData[field]}
      example={example}
      heading={`${heading} `}
      height="45px"
      error={errors[field]}
      disabled={disabled}
    />
  );

  const renderDateInput = (field, heading, maxDate) => (
    <DateInput
      heading={`${heading} `}
      value={formData[field]}
      onChange={(value) => handleChange(field, value)}
      maxDate={maxDate}
      height="45"
      error={errors[field]}
    />
  );

  const renderCheckbox = (label, field) => {
    const handleCheckboxClick = () => {
      handleChange(field, !formData[field]);
    };

    const handleLabelClick = (e) => {
      e.stopPropagation();

      if (field === "byLaw") {
        const teamsUrl =
          "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-bye-laws.pdf";
        window.open(teamsUrl, "_blank");
      } else if (field === "byRules") {
        const teamsUrl =
          "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-business-rules.pdf";
        window.open(teamsUrl, "_blank");
      }
    };

    return (
      <div className={Styles.checkSelction} onClick={handleCheckboxClick}>
        {formData[field] ? (
          <CheckBoxIcon style={{ fontSize: "24px", color: "var(--primary-color-text)" }} />
        ) : (
          <CheckBoxOutlineBlankIcon
            style={{ fontSize: "24px", color: "#6f6d7c" }}
          />
        )}
        <div>
          <span>I Understand and Accept the</span>
          <span
            style={{
              color: "var(--primary-color-text)",
              paddingLeft: "8px",
              cursor:
                field === "byLaw" || field === "byRules"
                  ? "pointer"
                  : "default",
            }}
            onClick={
              field === "byLaw" || field === "byRules"
                ? handleLabelClick
                : undefined
            }
          >
            {label}
          </span>
          {(field === "byLaw" || field === "byRules") && (
            <span
              style={{ color: "#666", fontSize: "12px", display: "block" }}
            ></span>
          )}
        </div>
        {errors[field] && (
          <span className={Styles.errorPass} style={{ display: "block", marginLeft: "32px", marginTop: "-6px" }}>
            {errors[field]}
          </span>
        )}
      </div>
    );
  };

  const renderCheckboxTrader = (field, options, heading) => (
    <div className={Styles.checkBoxElem}>
      <div className={Styles.checkBoxHead}>{heading}</div>
      <div
        className={`${Styles.selectCheckElemEent} ${Styles.checkboxGenerator}`}
      >
        {(Array.isArray(options) ? options : []).map((option) => (
          <div
            key={option.value}
            onClick={() => handleChange(field, option.value)}
            style={{ width: `${100 / options.length}%` }}
            className={
              formData[field] === option.value
                ? Styles.activeCheck
                : Styles.inactiveCheck
            }
          >
            {option.label}
          </div>
        ))}
      </div>
      {errors[field] && (
        <span className={Styles.errorPass}>{errors[field]}</span>
      )}
    </div>
  );

  useEffect(() => {
    // Only update if userData.userCompany actually exists and has changed
    if (userData?.userCompany) {
      setFormData((prevData) => ({
        // Locked to "Others": whatever the forced-selection effect already put
        // in state wins over the saved company value.
        holdingCompany:
          prevData.holdingCompany ||
          userData?.userCompany?.holdingCompany?.id ||
          userData?.userCompany?.holdingCompany ||
          "",
        traderType:
          userData?.userCompany?.traderType || prevData.traderType || {},
        companyName:
          userData?.userCompany?.companyName || prevData.companyName || "",
        displayName:
          userData?.userCompany?.displayName || prevData.displayName || "",
        companyDescription:
          userData?.userCompany?.companyDescription ||
          prevData.companyDescription ||
          "",
        category: userData?.userCompany?.category || prevData.category || {},
        registration:
          userData?.userCompany?.registration || prevData.registration || "",
        pan: userData?.userCompany?.pan || prevData.pan || "",
        tan: userData?.userCompany?.tan || prevData.tan || "",
        gst: userData?.userCompany?.gst || prevData.gst || "",
        license: userData?.userCompany?.license || prevData.license || "",
        bankName: userData?.userCompany?.bankName || prevData.bankName || "",
        bankAddress:
          userData?.userCompany?.bankAddress || prevData.bankAddress || "",
        payee: userData?.userCompany?.payee || prevData.payee || "",
        accountNo: userData?.userCompany?.accountNo || prevData.accountNo || "",
        ifsc: userData?.userCompany?.ifsc || prevData.ifsc || "",
        rating: userData?.userCompany?.rating || prevData.rating || {},
        ratingYear:
          userData?.userCompany?.ratingYear || prevData.ratingYear || "",
        agency: userData?.userCompany?.agency || prevData.agency || "",
        byRules: userData?.userCompany?.byRules || prevData.byRules || false,
        byLaw: userData?.userCompany?.byLaw || prevData.byLaw || false,
      }));
    }
  }, [userData?.userCompany]);

  // Holding Company is locked to "Others" for now: force-select it as soon as
  // the public list is available, and keep it selected on any later reset.
  useEffect(() => {
    const list = publicData?.holdingCompany;
    if (!Array.isArray(list) || list.length === 0) return;

    const othersOption = list.find((ele) => {
      const label =
        typeof ele === "object" && ele !== null
          ? ele.name ?? ele.value ?? ele.type ?? ele.entityName ?? ""
          : ele ?? "";
      return String(label).trim().toLowerCase() === "others";
    });
    if (!othersOption) return;

    setFormData((prevData) =>
      prevData.holdingCompany === othersOption
        ? prevData
        : { ...prevData, holdingCompany: othersOption },
    );
  }, [publicData?.holdingCompany, formData.holdingCompany]);

  return (
    <div className={Styles.elementMain}>
      <div className={Styles.displayInputSection}>
        <div className={Styles.conterntHeader}>Company Details</div>
        <div className={Styles.detailsInpMainContainer}>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("companyName", "Company Name *", "Eg. GNA Energy")}
            {renderTextInput("displayName", "Company Display Name *", "Eg. GNA")}
            {renderTextInput(
              "companyDescription",
              "Company Description",
              "Eg. India's Premier OTC Platform for Renewable Energy, Battery Energy, Storage Systems (BESS)",
            )}
          </div>
          <div className={Styles.layer1ElementsContainer}></div>
          <div className={Styles.layer3ElementsContainer}>
            {renderSelectInput(
              "holdingCompany",
              "Holding Company",
              publicData?.holdingCompany,
              "Eg. Select Holding Company",
              true,
            )}
            {renderSelectInput(
              "category",
              "Category *",
              publicData?.category,
              "Eg. Select Category",
            )}
            {renderDateInput(
              "registration",
              "Date of Incorporation/Registration *",
              new Date(),
            )}
          </div>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("gst", "GST Number *", "Enter GST Number")}
            {renderTextInput("pan", "PAN Number *", "Enter PAN Number")}
            {renderTextInput("tan", "TAN Number *", "Enter TAN Number")}
          </div>

          {userData?.profile?.case === "trader" && (
            <div className={Styles.layer3ElementsContainer}>
              {renderTextInput(
                "license",
                "Trading License Number *",
                "Enter Trading License",
              )}
              {renderCheckboxTrader(
                "traderType",
                [
                  { value: true, label: "InterState" },
                  { value: false, label: "IntraState" },
                ],
                "Trader Type",
              )}
              <div></div>
            </div>
          )}
          <div className={Styles.divider}></div>
          <div className={Styles.subConterntHeader}>Company Bank Details</div>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput(
              "accountNo",
              "Account Number *",
              "Enter Account Number",
            )}
            {renderTextInput("payee", "Payee Name *", "Enter Payee Name")}
            {renderIfscInput()}
          </div>
          <div className={Styles.layer2ElementsContainer}>
            {renderAutofillInput("bankName", "Bank Name *", "Enter Bank Name")}
            {renderAutofillInput(
              "bankAddress",
              "Branch Address *",
              "Enter Branch Address",
            )}
          </div>

          <div className={Styles.divider}></div>
          <div className={Styles.layer3ElementsContainer}>
            {renderSelectInput(
              "rating",
              "Credit Rating",
              publicData?.publicRating,
              "Eg. Select Credit Rating",
            )}
            {renderTextInput(
              "ratingYear",
              "Credit Rating Year",
              "Eg. FEB 2025",
            )}
            {renderTextInput(
              "agency",
              "Credit Rating Agency",
              "Eg. GNA Energy",
            )}
          </div>
          <div className={Styles.divider}></div>
          {renderCheckbox("Business Rules", "byRules")}
          {renderCheckbox("Bye Laws", "byLaw")}
        </div>
      </div>
      <div className={Styles.buttonSection}>
        <div className={Styles.prevBtn} onClick={() => handleProceed("prev")}>
          Back
        </div>
        <div className={Styles.nextBtn} onClick={() => handleContinue("next")}>
          Next
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
